import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FormRow from "../components/FormRow";
import InfoTip from "../components/InfoTip";
import {
  alertErrorClass,
  formControlClass,
  formControlErrorClass,
  formErrorTextClass,
} from "../../../dashboardStyles";
import { DURATION_PRESETS } from "../utils/serviceHelpers";
import {
  SERVICE_DURATION_MAX,
  SERVICE_DURATION_MIN,
  serviceFieldId,
} from "../utils/validation";

const chipClass = (active) =>
  `inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border px-3 text-sm font-semibold whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-ink-200 bg-white text-ink-700 hover:border-brand-300"
  }`;

/**
 * The open service's name and duration. Keyed by the service in the parent, so
 * "other duration" being open is remembered per service, not carried across.
 */
const ServiceForm = ({ service, errors, onUpdate }) => {
  const { t } = useTranslation();
  const nameId = serviceFieldId("name", service.id);
  const durationId = serviceFieldId("duration", service.id);
  const nameError = errors[nameId];
  const durationError = errors[durationId];
  const isPreset = DURATION_PRESETS.includes(Number(service.duration));
  const [customOpen, setCustomOpen] = useState(!isPreset);
  const showCustom = customOpen || !isPreset;

  const pickPreset = (minutes) => {
    setCustomOpen(false);
    onUpdate(service.id, "duration", minutes);
  };

  return (
    <div className="flex flex-col gap-4">
      <FormRow htmlFor={nameId} label={t("Dashboard.Wizard.Steps.services.nameLabel")}>
        <input
          type="text"
          id={nameId}
          placeholder={t("Dashboard.Setup.services.namePlaceholder")}
          value={service.name}
          onChange={(e) => onUpdate(service.id, "name", e.target.value)}
          className={nameError ? formControlErrorClass : formControlClass}
          aria-invalid={nameError ? "true" : undefined}
          aria-describedby={nameError ? `${nameId}-error` : undefined}
        />
        {nameError && (
          <span id={`${nameId}-error`} className={formErrorTextClass}>
            {nameError}
          </span>
        )}
      </FormRow>

      <FormRow
        labelAs="span"
        label={t("Dashboard.Wizard.Steps.services.durationLabel")}
        info={
          <InfoTip
            label={t("Dashboard.Setup.moreAbout", {
              label: t("Dashboard.Wizard.Steps.services.durationLabel"),
            })}
            text={t("Dashboard.Setup.services.durationTip")}
          />
        }
      >
        <div
          role="group"
          aria-label={t("Dashboard.Wizard.Steps.services.durationLabel")}
          className="grid grid-cols-3 gap-2 sm:grid-cols-5"
        >
          {DURATION_PRESETS.map((minutes) => (
            <button
              key={minutes}
              type="button"
              aria-pressed={!showCustom && Number(service.duration) === minutes}
              onClick={() => pickPreset(minutes)}
              className={chipClass(!showCustom && Number(service.duration) === minutes)}
            >
              {t("Dashboard.Setup.services.minutes", { count: minutes })}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={showCustom}
            onClick={() => setCustomOpen(true)}
            className={`${chipClass(showCustom)} col-span-2 sm:col-span-1`}
          >
            {t("Dashboard.Setup.services.otherDuration")}
          </button>
        </div>

        {showCustom && (
          <div className="mt-3 flex items-center gap-2">
            <label htmlFor={durationId} className="sr-only">
              {t("Dashboard.Setup.services.customDurationLabel")}
            </label>
            <div className="w-28 shrink-0">
              <input
                type="number"
                id={durationId}
                inputMode="numeric"
                min={SERVICE_DURATION_MIN}
                max={SERVICE_DURATION_MAX}
                step="5"
                value={service.duration || ""}
                onChange={(e) =>
                  onUpdate(service.id, "duration", parseInt(e.target.value, 10) || 0)
                }
                className={durationError ? formControlErrorClass : formControlClass}
                aria-invalid={durationError ? "true" : undefined}
                aria-describedby={durationError ? `${durationId}-error` : undefined}
              />
            </div>
            <span className="text-sm text-ink-500">
              {t("Dashboard.Setup.services.minutesUnit")}
            </span>
          </div>
        )}
        {durationError && (
          <span id={`${durationId}-error`} className={formErrorTextClass}>
            {durationError}
          </span>
        )}
      </FormRow>
    </div>
  );
};

ServiceForm.propTypes = {
  service: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

/**
 * Step 4: what customers can book — a name and how long it takes, one service
 * at a time. Every service is listed under the form as a row; pressing one
 * opens it above.
 */
const ServicesStep = ({
  services,
  activeId,
  errors,
  onSelect,
  onAdd,
  onUpdate,
  onRemove,
}) => {
  const { t } = useTranslation();
  const active = services.find((s) => s.id === activeId) ?? services[0];

  const hasErrors = (service) =>
    ["name", "duration"].some((field) => errors[serviceFieldId(field, service.id)]);

  return (
    <div className="mx-auto max-w-2xl">
      <h2 className="m-0 text-center text-2xl font-bold text-ink-900 sm:text-3xl">
        {t("Dashboard.Setup.services.title")}
      </h2>
      <p className="mx-auto mt-2 mb-6 max-w-xl text-center text-fluid-sm text-ink-500">
        {t("Dashboard.Setup.services.subtitle")}
      </p>

      {errors.services && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errors.services}</span>
        </div>
      )}

      <div className="rounded-card border border-ink-200 bg-white p-4 sm:p-5">
        {active && (
          <ServiceForm
            key={active.id}
            service={active}
            errors={errors}
            onUpdate={onUpdate}
          />
        )}

        {services.length > 0 && (
          <ul
            className="m-0 mt-5 flex list-none flex-col gap-2 border-t border-ink-100 p-0 pt-4"
            aria-label={t("Dashboard.Setup.services.listLabel")}
          >
            {services.map((service) => {
              const isActive = service === active;
              const name =
                service.name?.trim() ||
                t("Dashboard.Setup.services.untitled");
              return (
                <li key={service.id} className="flex items-stretch gap-2">
                  <button
                    type="button"
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => onSelect(service.id)}
                    className={`flex min-h-12 min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-left transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                      isActive
                        ? "border-brand-600 bg-brand-50"
                        : hasErrors(service)
                          ? "border-red-300 bg-red-50 hover:border-red-400"
                          : "border-ink-200 bg-ink-50 hover:border-brand-300"
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-ink-900">
                        {name}
                      </span>
                      <span className="block text-xs text-ink-500">
                        {t("Dashboard.Setup.services.minutes", {
                          count: Number(service.duration) || 0,
                        })}
                      </span>
                    </span>
                    <i
                      className={`bi ${isActive ? "bi-pencil-fill text-brand-600" : "bi-chevron-right text-ink-400"}`}
                      aria-hidden="true"
                    />
                  </button>
                  {services.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onRemove(service.id)}
                      aria-label={t("Dashboard.Wizard.Steps.services.removeAriaLabel", {
                        name:
                          service.name ||
                          t("Dashboard.Wizard.Steps.services.serviceFallbackName"),
                      })}
                      className="inline-flex w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-ink-200 bg-white text-ink-500 transition hover:border-red-500 hover:text-red-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                    >
                      <i className="bi bi-trash" aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <button
        type="button"
        className="mt-4 flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-card border-2 border-dashed border-ink-200 bg-white text-sm font-semibold text-brand-700 transition hover:border-brand-600 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        onClick={onAdd}
      >
        <i className="bi bi-plus-lg" aria-hidden="true" />
        {t("Dashboard.Wizard.Steps.services.addButton")}
      </button>

      <p className="m-0 mt-4 flex items-start gap-2 text-xs text-ink-500">
        <i className="bi bi-info-circle mt-px shrink-0" aria-hidden="true" />
        {t("Dashboard.Setup.services.durationNote")}
      </p>
    </div>
  );
};

ServicesStep.propTypes = {
  services: PropTypes.array.isRequired,
  /** The service open in the form; the first one when unset. */
  activeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  errors: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ServicesStep;
